import React, { useState, useEffect, useReducer, useContext } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import socket from "../../services/socket-io";
import { makeStyles } from "@material-ui/core/styles";
import { getUserColor } from "../../helpers/colorGenerator";
import {
  Table, TableBody, TableCell, TableHead, TableRow, Paper, Button,
  TextField, InputAdornment, Chip, Select, MenuItem, FormControl, InputLabel,
  IconButton, Tooltip, Typography, Avatar
} from "@material-ui/core";
import { 
  Edit as EditIcon, 
  DeleteOutline as DeleteIcon, 
  Visibility as VisibilityIcon,
  Autorenew as AutorenewIcon,
  Search as SearchIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon
} from "@material-ui/icons";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { useTitle } from "../../hooks/useTitle";
import { useHistory } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal/";
import ScheduleMessageModal from "../../components/ScheduleMessageModal";
import ContactSelect from "../../components/ContactSelect";
import WhatsAppSelect from "../../components/WhatsAppSelect";

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const scheduledMessages = action.payload;
    const newMessages = [];
    scheduledMessages.forEach((msg) => {
      const msgIndex = state.findIndex((m) => m.id === msg.id);
      if (msgIndex !== -1) {
        state[msgIndex] = msg;
      } else {
        newMessages.push(msg);
      }
    });
    return [...state, ...newMessages];
  }
  if (action.type === "UPDATE_MESSAGES") {
      const message = action.payload;
      const msgIndex = state.findIndex((m) => m.id === message.id);

      if (msgIndex !== -1) {
        state[msgIndex] = message;
        return [...state];
      } else {
        return [message, ...state];
      }
  }
  if (action.type === "DELETE_MESSAGE") {
    const messageId = action.payload;
    return state.filter((m) => m.id !== messageId);
  }
  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: theme.spacing(2),
    overflow: "hidden"
  },
  filtersPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0, 
  },
  filtersContainer: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
  },
  tablePaper: {
    flex: 1, 
    display: "flex",
    flexDirection: "column",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    overflow: "hidden"
  },
  tableContainer: {
    flex: 1,
    overflow: "auto", 
    ...theme.scrollbarStyles,
  },
  table: {
    minWidth: 1000, 
    "& th": {
      position: "sticky",
      top: 0,
      backgroundColor: theme.palette.background.paper,
      zIndex: 10,
      borderBottom: "2px solid " + theme.palette.divider,
    }
  },
  orphanRow: {
    backgroundColor: theme.palette.type === "dark" 
      ? "rgba(255, 152, 0, 0.15)"
      : "#fffde7",
    "&:hover": {
       backgroundColor: theme.palette.type === "dark" 
        ? "rgba(255, 152, 0, 0.25) !important" 
        : "#fff9c4 !important",
    }
  },
  statusChip: {
    fontWeight: "bold",
    height: 24,
  },
  userCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  userAvatar: {
    width: 24,
    height: 24,
    fontSize: 12,
  },
  messageBody: {
    maxWidth: 300,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
  }
}));

const statusColors = {
  PENDING: "#ff9800",
  SENT: "#4caf50",
  ERROR: "#f44336",
};

const statusTranslations = {
  PENDING: "Pendiente",
  SENT: "Enviado",
  ERROR: "Error",
};

const recurrenceTranslations = {
  DAILY: "Diario",
  WEEKLY: "Semanal",
  MONTHLY: "Mensual",
  ANNUALLY: "Anual",
};

const ScheduledMessages = () => {
  useTitle("Mensajes Programados");
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const history = useHistory(); 
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [scheduledMessages, dispatch] = useReducer(reducer, []);

  const [filterStatus, setFilterStatus] = useState("");
  const [filterContactId, setFilterContactId] = useState("");
  const [filterWhatsappId, setFilterWhatsappId] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingMessage, setDeletingMessage] = useState(null);
  
  const isAdmin = user && (user.profile === "admin" || user.profile === "superadmin");

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, filterStatus, filterContactId, filterWhatsappId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/scheduled-messages", {
            params: {
              searchParam,
              pageNumber,
              status: filterStatus,
              contactId: filterContactId,
              whatsappId: filterWhatsappId,
            },
          });
          dispatch({ type: "LOAD_MESSAGES", payload: data.scheduledMessages });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchMessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, filterStatus, filterContactId, filterWhatsappId]);

  useEffect(() => {
    const handleConnect = () => {
      socket.emit("joinNotification"); 
    };
    
    const eventHandlerName = "scheduledMessage";
    
    const handleScheduledMessage = (data) => {
      if (searchParam || filterStatus || filterContactId || filterWhatsappId) {
        return;
      }

      if (data.action === "create") {
        const newMessage = data.message;
        if (!newMessage.user && newMessage.userId === user.id) {
          newMessage.user = { id: user.id, name: user.name }; 
        }
        dispatch({ type: "UPDATE_MESSAGES", payload: newMessage });
      }
      
      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGES", payload: data.message });
      }
    };

    socket.on("connect", handleConnect);
    
    socket.on(eventHandlerName, handleScheduledMessage);

    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off(eventHandlerName, handleScheduledMessage);
    };
  }, [user, searchParam, filterStatus, filterContactId, filterWhatsappId]);
  
  const handleGoToMessage = (message) => {
    if (!message.ticketId || !message.messageId) return;
    history.push("/tickets/" + message.ticketId + "?messageId=" + message.messageId);
  };
  
  const handleOpenModal = () => {
    setSelectedMessageId(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedMessageId(null);
    setModalOpen(false);
    setPageNumber(1);
  };

  const handleEditMessage = (messageId) => {
    setSelectedMessageId(messageId);
    setModalOpen(true);
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await api.delete("/scheduled-messages/" + messageId);
      dispatch({ type: "DELETE_MESSAGE", payload: messageId });
      toast.success("Mensaje programado cancelado correctamente.");
    } catch (err) {
      toastError(err);
    }
    setDeletingMessage(null);
  };

  const loadMore = () => setPageNumber((prev) => prev + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .replace(/\s+/, " ")
      .split(" ")
      .slice(0, 2)
      .map((v) => v && v[0].toUpperCase())
      .join("");
  };

  return (
    <MainContainer>
      <ScheduleMessageModal
        open={modalOpen}
        onClose={handleCloseModal}
        scheduledMessageId={selectedMessageId}
      />
      <ConfirmationModal
        title={deletingMessage ? "Cancelar Mensaje Programado" : ""}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteMessage(deletingMessage.id)}
      >
        ¿Está seguro de que desea cancelar este mensaje programado?
      </ConfirmationModal>

      <MainHeader>
        <Title>Mensajes Programados</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" onClick={handleOpenModal} startIcon={<ScheduleIcon />}>
            Programar Nuevo
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      
      <div className={classes.root}>
        <Paper className={classes.filtersPaper} variant="outlined">
          <div className={classes.filtersContainer}>
            <TextField
              label="Buscar..."
              variant="outlined"
              size="small"
              style={{ flexGrow: 1, minWidth: 200 }}
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
              <InputLabel>Estado</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Estado"
                MenuProps={{
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" }
                }}
              >
                <MenuItem value=""><em>Todos</em></MenuItem>
                <MenuItem value="PENDING">Pendiente</MenuItem>
                <MenuItem value="SENT">Enviado</MenuItem>
                <MenuItem value="ERROR">Error</MenuItem>
              </Select>
            </FormControl>
            <div style={{ minWidth: 250 }}>
              <ContactSelect
                value={filterContactId}
                onChange={(contactId) => setFilterContactId(contactId)}
              />
            </div>
            <div style={{ minWidth: 200 }}>
               <WhatsAppSelect
                value={filterWhatsappId}
                onChange={(whatsappId) => setFilterWhatsappId(whatsappId)}
                showOrphanOption={true}
              />
            </div>
          </div>
        </Paper>
      
        <Paper className={classes.tablePaper} variant="outlined">
          <div className={classes.tableContainer} onScroll={handleScroll}>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>Contacto</TableCell>
                  {isAdmin && <TableCell>Usuario</TableCell>}
                  <TableCell>Mensaje</TableCell>
                  <TableCell align="center">Próximo Envío</TableCell>
                  <TableCell align="center">Recurrencia</TableCell>
                  <TableCell align="center">Conexión</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell align="center" style={{ width: 140 }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scheduledMessages.map((msg) => {
                  const isOrphan = !msg.whatsappId || !msg.whatsapp;
                  const creatorName = msg.user ? msg.user.name : "Sistema";

                  return (
                    <TableRow 
                      key={msg.id}
                      hover
                      className={isOrphan ? classes.orphanRow : ""}
                    >
                      <TableCell style={{ fontWeight: 500 }}>
                        {msg.contact?.name || "Desconocido"}
                      </TableCell>

                      {isAdmin && (
                        <TableCell>
                          <div className={classes.userCell}>
                            <Avatar 
                              className={classes.userAvatar}
                              style={{ 
                                  backgroundColor: getUserColor(creatorName),
                                  color: "#fff",
                                  fontWeight: "bold"
                              }}
                            >
                              {getInitials(creatorName)}
                            </Avatar>
                            <Typography variant="body2">{creatorName}</Typography>
                          </div>
                        </TableCell>
                      )}

                      <TableCell>
                        <Tooltip title={msg.body} arrow>
                          <div className={classes.messageBody}>
                            {msg.body}
                          </div>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {format(parseISO(msg.sendAt), "dd/MM/yyyy HH:mm")}
                      </TableCell>
                      <TableCell align="center">
                        {msg.recurrence && (
                          <Chip
                            icon={<AutorenewIcon style={{ fontSize: 16 }} />}
                            label={recurrenceTranslations[msg.recurrence] || msg.recurrence}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {isOrphan ? (
                            <Tooltip title="Sin conexión asignada">
                                <WarningIcon style={{ color: "#ff9800" }} />
                            </Tooltip>
                        ) : (
                            <Typography variant="caption">{msg.whatsapp?.name}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={statusTranslations[msg.status] || msg.status}
                          size="small"
                          className={classes.statusChip}
                          style={{ 
                              backgroundColor: statusColors[msg.status] || "#9e9e9e", 
                              color: "#fff" 
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" style={{ whiteSpace: "nowrap" }}>
                        <Tooltip title="Ver en Chat">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleGoToMessage(msg)}
                              disabled={!msg.messageId || msg.status !== "SENT"}
                              color="primary"
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleEditMessage(msg.id)}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancelar / Eliminar">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeletingMessage(msg);
                              setConfirmModalOpen(true);
                            }}
                            style={{ color: "#f44336" }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                
                {scheduledMessages.length === 0 && !loading && (
                   <TableRow>
                      <TableCell colSpan={isAdmin ? 8 : 7} align="center" style={{ padding: 40 }}>
                         <Typography color="textSecondary">No hay mensajes programados.</Typography>
                      </TableCell>
                   </TableRow>
                )}

                {loading && <TableRowSkeleton columns={isAdmin ? 8 : 7} />}
              </TableBody>
            </Table>
          </div>
        </Paper>
      </div>
    </MainContainer>
  );
};

export default ScheduledMessages;
